import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";

import { RISK_CONFIG, RISK_SCALE_ORDER } from "../../constants/riskConfig";
import { EASE } from "../PageTransition";
import { KICKER } from "./shell";

/**
 * The homepage's scale, as a few places to try (design file, D5 option D).
 *
 * Six example places, each a card that reveals its answer — a band, its name,
 * and one plain sentence. Between them they cover all five bands and the
 * blank, so revealing them all shows the whole scale, and the legend beneath
 * lights each band as it is revealed. Colour arrives only with an answer.
 *
 * The places are kinds of place, not addresses, and they are examples, not
 * findings: no card says anything about a real town's water. The section says
 * so beside the cards.
 *
 * Each card is a button (Enter / Space as well as a tap). Left alone on
 * screen, the cards reveal themselves one at a time, hold for five seconds,
 * hide, and go again; the first interaction ends that for good, so a reader's
 * own choices stay as they left them. Under reduced motion every card starts
 * revealed and nothing plays.
 *
 * The band sentences are draft, kept general; they should come from the
 * product's own guidance before this ships.
 */
const PLACES = [
  { id: "iowa", kind: "A farm town", where: "in Iowa", band: "0" },
  { id: "arizona", kind: "A city", where: "in Arizona", band: "1" },
  { id: "boston", kind: "A suburb", where: "of Boston", band: "2" },
  { id: "carolina", kind: "A coastal town", where: "in North Carolina", band: "-2" },
  { id: "michigan", kind: "A mill town", where: "in Michigan", band: "-1" },
  { id: "montana", kind: "A small town", where: "in Montana", band: null },
];

const SAY = {
  2: "Nothing of concern in the tests on record.",
  1: "Small amounts, well under the limits.",
  0: "Found, and well under the limit. Worth knowing.",
  "-1": "At or near a limit. Worth acting on.",
  "-2": "Over a federal or state limit. Act now.",
};
const NOT_SCORED = "Nobody has tested here. A gap in the record, not good news.";

/** The autoplay's rhythm, in milliseconds. */
const FIRST_AT = 600;
const EVERY = 1200;
const HOLD = 5000;

const FACE = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.35, ease: EASE },
};

const Card = ({ place, revealed, onToggle, reduce }) => {
  const scored = place.band != null;
  const answer = scored ? RISK_CONFIG[place.band].label : "Not scored";
  const face = reduce ? {} : FACE;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={revealed}
      className={`group flex min-h-[14.5rem] flex-col justify-between rounded-md p-[18px] text-left transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-zp-link focus-visible:ring-offset-2 ${
        revealed
          ? `border bg-zp-bg ${scored ? "border-zp-ink/[0.08]" : "border-zp-ink/30"}`
          : "border border-transparent bg-zp-surface hover:bg-zp-tint"
      }`}
    >
      <span className="block">
        <span className={`${KICKER} block text-[9.5px] text-zp-ink`}>{place.kind}</span>
        <span className={`${KICKER} mt-1 block text-[9px] text-zp-slate`}>
          {place.where}
        </span>
      </span>

      <AnimatePresence mode="wait" initial={false}>
        {revealed ? (
          <motion.span key="answer" className="block w-full" {...face}>
            {scored ? (
              <span
                className="block h-[5px] rounded-[2px]"
                style={{ backgroundColor: RISK_CONFIG[place.band].hex }}
                aria-hidden="true"
              />
            ) : (
              <span
                className="block h-[5px] rounded-[2px] border border-zp-ink/40"
                aria-hidden="true"
              />
            )}
            <span className="mt-3 block font-display text-[1.5rem] font-light leading-none -tracking-[0.02em] text-zp-ink">
              {answer}
            </span>
            <span className="mt-2.5 block text-[12.5px] leading-snug text-zp-body">
              {scored ? SAY[place.band] : NOT_SCORED}
            </span>
          </motion.span>
        ) : (
          <motion.span key="hidden" className="block" {...face}>
            <span
              className="block font-mono text-[14px] tracking-[0.3em] text-zp-ink/35"
              aria-hidden="true"
            >
              ???
            </span>
            <span className={`${KICKER} mt-2 block text-[8.5px] text-zp-slate`}>
              Tap to reveal
            </span>
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
};

/** The scale, lit band by band as places reveal them. */
const Legend = ({ lit }) => (
  <div className="mt-7 flex flex-wrap items-start gap-x-4 gap-y-3" aria-hidden="true">
    <div className="grid min-w-0 flex-1 basis-full grid-cols-5 gap-[3px] sm:basis-auto">
      {RISK_SCALE_ORDER.map((score) => {
        const on = lit.has(score);
        return (
          <div key={score}>
            <div
              className="h-1.5 rounded-[2px] transition-colors duration-500"
              style={{ backgroundColor: on ? RISK_CONFIG[score].hex : undefined }}
              data-lit={on || undefined}
            >
              {!on && <div className="h-full rounded-[2px] bg-zp-tint" />}
            </div>
            <p
              className={`mt-1.5 font-mono text-[7.5px] uppercase tracking-[0.04em] sm:text-[9px] sm:tracking-[0.12em] ${
                on ? "text-zp-ink" : "text-zp-slate"
              }`}
            >
              {RISK_CONFIG[score].label}
            </p>
          </div>
        );
      })}
    </div>
    <div className="w-24">
      <div
        className={`h-1.5 rounded-[2px] border transition-colors duration-500 ${
          lit.has("none") ? "border-zp-ink/60" : "border-zp-ink/20"
        }`}
      />
      <p
        className={`mt-1.5 font-mono text-[7.5px] uppercase tracking-[0.04em] sm:text-[9px] sm:tracking-[0.12em] ${
          lit.has("none") ? "text-zp-ink" : "text-zp-slate"
        }`}
      >
        Not scored
      </p>
    </div>
  </div>
);

export default function ScaleReveal() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4 });
  const [revealed, setRevealed] = useState(() => new Set());
  const [touched, setTouched] = useState(false);

  // Reduced motion: everything shown, nothing plays.
  useEffect(() => {
    if (reduce) setRevealed(new Set(PLACES.map((p) => p.id)));
  }, [reduce]);

  // Autoplay while on screen and untouched: one at a time, hold, hide, again.
  useEffect(() => {
    if (reduce || touched || !inView) return undefined;
    const timers = [];
    const run = () => {
      setRevealed(new Set());
      PLACES.forEach((p, i) => {
        timers.push(
          setTimeout(
            () => setRevealed((prev) => new Set(prev).add(p.id)),
            FIRST_AT + i * EVERY,
          ),
        );
      });
      timers.push(setTimeout(run, FIRST_AT + PLACES.length * EVERY + HOLD));
    };
    run();
    return () => timers.forEach(clearTimeout);
  }, [reduce, touched, inView]);

  const toggle = (id) => {
    setTouched(true);
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const lit = new Set(
    PLACES.filter((p) => revealed.has(p.id)).map((p) =>
      p.band == null ? "none" : p.band,
    ),
  );

  return (
    <div ref={ref} data-testid="scale-reveal">
      {/* <p className={`${KICKER} text-[9.5px] text-zp-slate`}>
        Tap a place to see its answer · examples, not findings
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {PLACES.map((place) => (
          <Card
            key={place.id}
            place={place}
            revealed={revealed.has(place.id)}
            onToggle={() => toggle(place.id)}
            reduce={reduce}
          />
        ))}
      </div> */}
      <Legend lit={lit} />
    </div>
  );
}
